import { defineComponent, ref } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { box, container, loader, loadingButton, pageTitle } from '@/ui/components';
import { useTask } from '@/ui/concerns';
import { create } from '@/services/api/resources/sensor';
import { filter as fetchAllUsers } from '@/services/api/resources/user';
import { filter as fetchAllEntityItems } from '@/services/api/resources/entityItem';
import { filterListToOptions, pushRoute, sequence } from '@/ui/helpers/uiHelper';
import { hasFeature, hasPermission, Permissions } from '@/ui/auth/auth';
import { SelectOptions } from '@/ui/types';
import { useSensorForm } from './useSensorForm';
import { useSensorRecord } from './useSensorRecord';
import { renderForbiddenPage } from '../error/ForbiddenPage';
import { renderResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';
import { useCreatableUserModal } from '@/ui/pages/useCreatableUserModal';

export const SensorCreatePage = defineComponent({
  name: 'SensorCreatePage',
  setup() {
    if (!hasFeature('sensors')) {
      return renderResourceTeaserPage(t('common.sensors'));
    }

    if (!hasPermission(Permissions.MANAGE_HARDWARE)) {
      return renderForbiddenPage;
    }

    const userOptions = ref<SelectOptions>([]);
    const entityItemOptions = ref<SelectOptions>([]);

    // get record
    const getRecord = useTask(
      () => sequence(fetchAllUsers(), fetchAllEntityItems()),
      ([users, entityItems]) => {
        userOptions.value = filterListToOptions(users);
        entityItemOptions.value = filterListToOptions(entityItems);
      },
    );

    // create empty record
    const record = useSensorRecord();

    // send record
    const sendRecord = useTask(
      () => create(record.value),
      () => pushRoute({ name: 'sensors' }),
    );

    // form
    const { formFields, handleSubmit } = useSensorForm(record, sendRecord);

    useCreatableUserModal(userOptions, id => {
      record.value.alarmUsers.push(id);
    });

    return function render() {
      return container(
        [
          pageTitle(t('sensors.create')),
          box([
            loader('form', getRecord, [
              ...formFields(userOptions.value, entityItemOptions.value),
              loadingButton(
                t('sensors.form.create-btn'),
                handleSubmit,
                sendRecord.state.value.pending,
              ),
            ]),
          ]),
        ],
        'narrow',
      );
    };
  },
});
